import { useEffect, useState } from "react";
import { Icon } from "./icons";
import { cx } from "./ui";
import { useTranslation } from "react-i18next";

/**
 * Copies a string and says so for two seconds.
 *
 * The confirmation is the whole feature. A copy button that does nothing
 * visible is one people press three times, and on this dashboard the thing
 * being copied is a link somebody is about to paste somewhere public — so
 * "did that work" is a question worth answering before they find out.
 *
 * `navigator.clipboard` is unavailable on an insecure origin, which locally
 * means anything but `localhost`. The failure is silent by design: falling back
 * to a hidden textarea and `execCommand` is a deprecated path to support a case
 * that does not exist in production, and an error toast about clipboard
 * permissions is not something the reader can act on.
 */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2_000);
    // Cleared on unmount as well as on the timer, so a row removed while its
    // confirmation is showing does not set state on a component that is gone.
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => setCopied(true))
          .catch(() => {});
      }}
      className={cx(
        "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[13px] transition-colors duration-200 ease-smooth pointer-coarse:h-auto pointer-coarse:min-h-11",
        copied ? "text-mint" : "text-fg-muted hover:bg-raised hover:text-fg",
      )}
      aria-label={copied ? t("copied") : (label ?? t("copy"))}
    >
      <Icon name={copied ? "check" : "copy"} className="size-3.5" />
      {copied ? t("copied") : (label ?? t("copy"))}
    </button>
  );
}
